import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { FileTextIcon } from 'lucide-react'
import { toast } from 'sonner'

import { ApiError } from '@/api/errors'
import { queryKeys } from '@/api/keys'
import { quoteSettingsApi } from '@/api/quote-settings'
import type { QuoteSettingsInput } from '@/api/types'
import { requireAdmin } from '@/auth/guards'
import { EmptyState } from '@/components/empty-state'
import { Frame } from '@/components/frame'
import { QuoteSettingsForm } from '@/components/quotes/quote-settings-form'
import { Skeleton } from '@/components/ui/skeleton'

/**
 * Réglages › Soumissions, **admin** (lot 14, #208) : l'émetteur, les taxes,
 * les conditions de paiement, les motifs de refus, les défauts et le pied de
 * page. Une ressource unique, remplacée en entier à chaque enregistrement.
 */
export const Route = createFileRoute('/_app/settings/quotes')({
  beforeLoad: requireAdmin,
  component: SettingsQuotes,
})

function SettingsQuotes() {
  const queryClient = useQueryClient()
  const settings = useQuery({
    queryKey: queryKeys.quoteSettings,
    queryFn: () => quoteSettingsApi.get(),
  })

  const update = useMutation({
    mutationFn: (payload: QuoteSettingsInput) => quoteSettingsApi.update(payload),
    onSuccess: (saved) => {
      queryClient.setQueryData(queryKeys.quoteSettings, saved)
      // Le résolu d'un client (`billing.tax_rates`, `billing.payment_terms`) suit le paramétrage.
      void queryClient.invalidateQueries({ queryKey: queryKeys.clients.all })
      toast.success('Paramétrage des soumissions enregistré.')
    },
  })

  if (settings.isPending) {
    return (
      <Frame>
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </Frame>
    )
  }

  if (settings.isError) {
    return (
      <EmptyState
        icon={FileTextIcon}
        title="Impossible de charger le paramétrage"
        description={
          settings.error instanceof ApiError ? settings.error.message : 'Réessayez dans un instant.'
        }
      />
    )
  }

  return (
    <QuoteSettingsForm
      settings={settings.data}
      pending={update.isPending}
      error={update.error}
      onSubmit={async (payload) => {
        try {
          await update.mutateAsync(payload)
          return true
        } catch {
          return false
        }
      }}
    />
  )
}
